<template>
  <div class="dep" v-if="data || error">
    <div class="dep-head">
      <div class="dep-title"><v-icon icon="mdi-rocket-launch-outline" size="18"></v-icon> Deployment <span class="dep-pill" :class="'is-' + overall">{{ OVERALL[overall] }}</span></div>
      <div class="dep-meta">
        <span>Dashboard <b>{{ webVersion }}</b></span>
        <span v-if="data && data.app">API <b>{{ data.app.version || '—' }}</b><small v-if="data.app.sha"> ({{ data.app.sha }})</small> · up since {{ when(data.app.started_at) }}</span>
        <a v-if="data" :href="data.github.actions_url" target="_blank" rel="noopener">Builds ↗</a>
        <button type="button" class="dep-refresh" :disabled="busy" @click="load">{{ busy ? '…' : 'Refresh' }}</button>
      </div>
    </div>
    <div v-if="error" class="dep-warn">{{ error }}</div>
    <div v-else-if="data && !data.kubernetes.available" class="dep-warn">Cluster state unavailable: {{ data.kubernetes.error }}</div>
    <div v-else-if="data" class="dep-grid">
      <div v-for="d in data.kubernetes.deployments" :key="d.name" class="dep-card" :class="'is-' + d.state">
        <div class="dep-card__name">{{ label(d.name) }} <span class="dep-pill" :class="'is-' + d.state">{{ STATE[d.state] || d.state }}</span></div>
        <div class="dep-card__row"><span>Image</span><code>{{ d.tag }}</code></div>
        <div class="dep-card__row"><span>Pods ready</span><b>{{ d.ready }}/{{ d.desired }}</b><small v-if="d.updated < d.desired"> · {{ d.updated }} updated</small></div>
        <div class="dep-card__row"><span>Last change</span><span>{{ when(d.updated_at) }}</span></div>
        <small v-if="d.state !== 'healthy' && d.message" class="dep-msg">{{ d.message }}</small>
        <ul class="dep-pods">
          <li v-for="p in podsOf(d.name)" :key="p.name"><span class="dep-dot" :class="{ on: p.ready }"></span>{{ p.name.replace(d.name + '-', '') }} · {{ p.phase }}<small v-if="p.restarts"> · {{ p.restarts }} restarts</small><small v-if="p.tag !== d.tag"> · {{ p.tag }}</small></li>
        </ul>
      </div>
    </div>
    <small v-if="data" class="dep-foot">{{ data.flow }}</small>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { fetchWrapper } from '@/helpers';

const baseUrl = `${process.env.VUE_APP_VUE_API_URL}`;
const webVersion = process.env.VUE_APP_VERSION || 'dev';
const STATE = { healthy: 'Healthy', rolling: 'Rolling out', degraded: 'Degraded', failed: 'Rollout failed' };
const OVERALL = { healthy: 'All healthy', rolling: 'Deploying…', degraded: 'Degraded', failed: 'Rollout failed', unknown: '—' };
const data = ref(null), error = ref(''), busy = ref(false);
let timer = null;

const label = (n) => (/backend/.test(n) ? 'API (backend)' : /frontend/.test(n) ? 'Dashboard (frontend)' : n);
const when = (v) => (v ? new Date(v).toLocaleString() : '—');
const podsOf = (dep) => (data.value?.kubernetes?.pods || []).filter((p) => p.name.startsWith(dep + '-'));
const overall = computed(() => {
  const ds = data.value?.kubernetes?.deployments || [];
  if (!ds.length) return 'unknown';
  if (ds.some((d) => d.state === 'failed')) return 'failed';
  if (ds.some((d) => d.state === 'rolling')) return 'rolling';
  if (ds.some((d) => d.state === 'degraded')) return 'degraded';
  return 'healthy';
});
async function load() {
  busy.value = true;
  try { data.value = await fetchWrapper.get(`${baseUrl}/admin/platform/status`); error.value = ''; }
  catch (e) { error.value = `Deployment status unavailable: ${e?.message || e}`; }
  finally { busy.value = false; }
}
onMounted(() => { load(); timer = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 30000); });
onBeforeUnmount(() => clearInterval(timer));
</script>

<style scoped>
.dep { background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.08); border-radius: 12px; padding: 12px 14px; margin-bottom: 16px; display: grid; gap: 10px; }
.dep-head { display: flex; justify-content: space-between; gap: 12px; flex-wrap: wrap; align-items: center; }
.dep-title { font-weight: 700; font-size: 15px; display: flex; align-items: center; gap: 8px; }
.dep-meta { display: flex; gap: 14px; flex-wrap: wrap; align-items: center; font-size: 13px; opacity: .9; }
.dep-refresh { background: transparent; border: 1px solid rgba(255,255,255,.18); color: inherit; border-radius: 6px; padding: 3px 8px; cursor: pointer; font: inherit; font-size: 12px; }
.dep-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 10px; }
.dep-card { border: 1px solid rgba(255,255,255,.1); border-left-width: 4px; border-radius: 10px; padding: 10px 12px; display: grid; gap: 4px; font-size: 13px; }
.dep-card.is-healthy { border-left-color: #22c55e; } .dep-card.is-rolling { border-left-color: #f59e0b; } .dep-card.is-degraded, .dep-card.is-failed { border-left-color: #ef4444; }
.dep-card__name { font-weight: 600; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.dep-card__row { display: flex; justify-content: space-between; gap: 10px; opacity: .9; }
.dep-card__row code { font-size: 12px; }
.dep-pill { font-size: 11px; padding: 2px 8px; border-radius: 999px; border: 1px solid currentColor; font-weight: 600; }
.dep-pill.is-healthy { color: #22c55e; } .dep-pill.is-rolling { color: #f59e0b; } .dep-pill.is-degraded, .dep-pill.is-failed { color: #ef4444; } .dep-pill.is-unknown { opacity: .6; }
.dep-pods { list-style: none; margin: 4px 0 0; padding: 0; display: grid; gap: 2px; font-size: 12px; opacity: .85; }
.dep-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #ef4444; margin-right: 6px; } .dep-dot.on { background: #22c55e; }
.dep-msg { color: #fca5a5; }
.dep-warn { color: #fca5a5; font-size: 13px; }
.dep-foot { opacity: .65; font-size: 12px; }
</style>
